import {
    IExpressionResolver,
    ResolutionErrorKind,
    failedExpressionResolution,
    IExpressionResolution,
    IParsingError,
    ParsingErrorKind,
    successfulExpressionResolution,
    IObjectSchema,
    IApplicability
} from 'app/model/expressions/expression-resolution';
import { ScalarExpression, ExpressionCategory } from 'app/model/expressions/expression';
import { FunctionArgumentParser } from 'app/model/expressions/function-argument-parser';
import {
    getPropertyStack,
    matchPropertyStackWithSchema,
    findPattern,
    buildSubExpressionResolver,
    getInnerError
} from 'app/model/expressions/expression-utils';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Named, Owned } from 'app/model/base/named';
import { Metamodel } from 'app/model/base/model';
import { numberOfLeadingWhitespace } from 'app/shared/utils/string.util';

export interface IProperty {
    property: string;
    separator: string;
}

export interface ILocatedEntity {
    isExcluded: boolean;
    named: Named;
}

/**
 * Resolves bracket expressions. e.g.: @activity('Lookup1').output.value[0].Prop_0
 */
export abstract class BracketExpressionResolver implements IExpressionResolver {
    protected readonly _entityFoundExcludedError: ResolutionErrorKind;
    protected abstract readonly _entityNotReferenceableResolutionError: ResolutionErrorKind;
    protected abstract readonly _missingEntityNameResolutionError: ResolutionErrorKind;

    /**
     * Initializes a new instance of the ActivityOutputResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _expressionResolversCallback Provides supported expression resolvers used to resolve function arguments.
     */
    public constructor(
        private _entity: Owned,
        private readonly _expressionResolversCallback: () => IExpressionResolver[]) {
    }

    public isOfType(expression: string, ignoreStart?: boolean): boolean {
        expression = expression ? expression.trim() : '';
        if (expression === '') {
            return false;
        }
        const prefix = this.getPrefix(ignoreStart);
        return expression.startsWith(prefix);
    }

    public resolve(expression: string, ignoreStart?: boolean): IExpressionResolution {
        if (!this.isOfType(expression, ignoreStart)) {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression });
        }

        // Don't trim left whitespace so the correct offset is known of an error (if there is one).
        const cleanedExpression = expression ? expression.trimRight() : '';

        // skip expression prefix and any left whitespace.
        let index = this.getPrefix(ignoreStart).length + numberOfLeadingWhitespace(cleanedExpression);

        if (index >= cleanedExpression.length || cleanedExpression.charAt(index) !== '(') {
            return failedExpressionResolution(expression, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError: {
                    error: ParsingErrorKind.OpeningParanthesisExpected,
                    startIndex: index
                }
            });
        }

        const openingParanthesisIndex = index++;
        let enclosingParanthesisIndex = findPattern(cleanedExpression, ')', index);
        if (enclosingParanthesisIndex < 0) {
            return failedExpressionResolution(expression, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError: {
                    error: ParsingErrorKind.MissingEnclosingParanthesis,
                    startIndex: index
                }
            });
        }

        let nextOpeningParanthesisIndex = findPattern(cleanedExpression, '(', index);
        // ignore nested () until we find the matching )
        while (nextOpeningParanthesisIndex > -1 && enclosingParanthesisIndex > -1 && nextOpeningParanthesisIndex < enclosingParanthesisIndex) {
            enclosingParanthesisIndex = findPattern(cleanedExpression, ')', enclosingParanthesisIndex + 1);
            nextOpeningParanthesisIndex = findPattern(cleanedExpression, '(', nextOpeningParanthesisIndex + 1);
        }

        try {
            // we expect the argument given to the activity function to be strictly a string literal
            const entityClauseParameters = new FunctionArgumentParser(
                openingParanthesisIndex + 1,
                buildSubExpressionResolver(this._entity, this._expressionResolversCallback))
                .parse(cleanedExpression.substring(openingParanthesisIndex + 1, enclosingParanthesisIndex));

            if (!entityClauseParameters || entityClauseParameters.length !== 1 || entityClauseParameters[0].kind !== 'StringLiteral') {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.SingleStringLiteralError,
                    startIndex: openingParanthesisIndex + 1,
                    endIndex: enclosingParanthesisIndex - 1
                });
            }

            // remove the enclosing quotes
            const entityName = entityClauseParameters[0].stringValue.substring(1, entityClauseParameters[0].stringValue.length - 1);

            if (!entityName) {
                return failedExpressionResolution(expression, {
                    kind: this._missingEntityNameResolutionError,
                    startIndex: openingParanthesisIndex + 1,
                    endIndex: enclosingParanthesisIndex - 1
                });
            }

            const matchingEntity = this.locateEntity(this._entity, entityName);

            if (!matchingEntity) {
                return failedExpressionResolution(expression, {
                    kind: this._entityNotReferenceableResolutionError,
                    startIndex: openingParanthesisIndex + 1,
                    endIndex: enclosingParanthesisIndex - 1,
                    details: { entityName }
                });
            }

            index = enclosingParanthesisIndex + 1;

            return this.resolveOutput(matchingEntity, expression, cleanedExpression.substring(index), index);
        } catch (error) {
            const parsingError = error as IParsingError;
            const innerError = getInnerError(parsingError);

            return innerError || failedExpressionResolution(expression, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError
            });
        }
    }

    public checkApplicability(entity: Owned): IApplicability {
        return { isApplicable: Metamodel.modelOf(entity.concept) === Pipeline.MODEL };
    }

    public getEntity(): Owned {
        return this._entity;
    }

    protected abstract locateEntity(fromEntity: Owned, entityName: string):  ILocatedEntity;
    protected abstract getPrefix(ignoreStart: boolean): string;

    /**
     * Checks if accessing the given properties from the entity is valid.
     * @param matchedEntity entity being validated.
     * @param propertyNestings properties of the entity being accessed.
     * @returns The error type that was encountered. If there is no error, undefined is returned.
     */
    protected checkPropertyValidity(matchedEntity: ILocatedEntity, propertyNestings: IProperty[]): ResolutionErrorKind {
        return undefined;
    }

    private resolveOutput(matchedEntity: ILocatedEntity, expressionSource: string, suffix: string, offset: number): IExpressionResolution {
        const activityOutputSchema: IObjectSchema = { expressionType: 'Any' };
        const entity = matchedEntity.named;

        // get the nested properties
        let propertyNestings: Array<{ property: string, separator: string }>;

        try {
            propertyNestings = getPropertyStack(suffix, offset);
        } catch (parsingError) {
            return failedExpressionResolution(suffix, {
                kind: ResolutionErrorKind.InvalidSyntax,
                parsingError
            });
        }

        const errorFound = this.checkPropertyValidity(matchedEntity, propertyNestings);
        if (errorFound) {
            return failedExpressionResolution(expressionSource, {
                kind: errorFound,
                startIndex: offset,
                details: { entityName: matchedEntity.named.name }
            });
        }

        const subExpressionResolver = buildSubExpressionResolver(this._entity, this._expressionResolversCallback);
        const propertyExpressionResolution = matchPropertyStackWithSchema(suffix, propertyNestings, activityOutputSchema, offset, subExpressionResolver);

        if (propertyExpressionResolution.isValid) {
            return successfulExpressionResolution(expressionSource,
                new ScalarExpression(
                    ExpressionCategory.ActivityOutput,
                    `${entity.name} output`,
                    expressionSource,
                    `${entity.name} output`,
                    (propertyExpressionResolution.expression as ScalarExpression).returnType,
                    (propertyExpressionResolution.expression as ScalarExpression).returnSubType),
                [entity]);
        } else {
            return propertyExpressionResolution;
        }
    }
}
